import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiStar, HiSearch, HiShoppingCart, HiChevronDown } from 'react-icons/hi';
import { productsAPI, storesAPI } from '../services/api';
import { notify } from '../utils/notify';
import { mensajeDeError } from '../utils/apiError';
import { PRODUCT_IMAGE_FALLBACK } from '../utils/imagePlaceholder';
import { dinero } from '../utils/economia';
import { comisionDe } from '../utils/comision';
import ProductDetailsModal from '../components/ProductDetailsModal';
import { desdeCandidato } from '../utils/productoVista';

/**
 * Productos ganadores.
 *
 * Antes esta página cogía los cien últimos productos que alguien había buscado,
 * le pedía a Gemini una nota «siendo realista» sin nombrarle ningún criterio, y
 * enseñaba los que pasaran de 60. No buscaba nada, y de 289 productos en la
 * caché solo uno llegó a tener nota.
 *
 * Ahora eliges un nicho, se busca en el catálogo del proveedor, se puntúa con
 * los cinco criterios del sector —tres calculados, dos preguntados— y la nota
 * se enseña desglosada: un número solo se cree o no se cree; un desglose se
 * discute.
 */

const NICHOS = ['salud', 'belleza', 'cuidado personal'];

// Cuántos candidatos se evalúan a fondo de una tanda. Cada uno son dos
// consultas al proveedor —que admite una por segundo— más una a la IA: unos
// tres segundos por producto. Diez es medio minuto de espera; el resto queda a
// un botón de distancia para quien quiera seguir mirando.
const A_EVALUAR = 10;

const COLUMNAS = [
    { clave: 'nota', etiqueta: 'score', numerica: true },
    { clave: 'costo', etiqueta: 'cost', numerica: true },
    { clave: 'envio', etiqueta: 'shipping', numerica: true },
    { clave: 'precio', etiqueta: 'price', numerica: true },
    { clave: 'beneficio', etiqueta: 'profit', numerica: true },
    { clave: 'vendedores', etiqueta: 'sellers', numerica: true },
    { clave: 'dias', etiqueta: 'days', numerica: true }
];

function colorDeNota(n) {
    if (n >= 80) return 'text-green-500';
    if (n >= 60) return 'text-amber-500';
    return 'text-red-500';
}

export default function WinningProducts() {
    const { t, i18n } = useTranslation();

    const [nicho, setNicho] = useState('');
    const [candidatos, setCandidatos] = useState([]);
    const [buscando, setBuscando] = useState(false);
    const [porEvaluar, setPorEvaluar] = useState(0);
    // Motivo por el que se dejo de evaluar. Cuando la cuota del dia se agota,
    // seguir con los demas productos solo gasta llamadas que ya no hay.
    const [cortado, setCortado] = useState(null);
    const [orden, setOrden] = useState({ campo: 'nota', desc: true });
    // Mientras evalúa, la tabla no se reordena sola: una fila que pasa de 15 a
    // 83 saltaba a lo alto y desplazaba todo lo que estabas leyendo. Se ordena
    // al terminar, o en cuanto pulses una columna.
    const [ordenManual, setOrdenManual] = useState(false);
    const [limiteEvaluacion, setLimiteEvaluacion] = useState(A_EVALUAR);
    const [notaMinima, setNotaMinima] = useState(0);
    const [abierto, setAbierto] = useState(null);
    const [ficha, setFicha] = useState(null);
    const [stores, setStores] = useState([]);
    const [importando, setImportando] = useState(new Set());

    useEffect(() => {
        storesAPI.getAll().then(
            (r) => setStores(r.data.data.stores || []),
            () => {}
        );
    }, []);

    const buscar = async (termino) => {
        const texto = (termino ?? nicho).trim();
        if (!texto) {
            notify.error(t('winning_products.choose_niche'));
            return;
        }

        setBuscando(true);
        setCandidatos([]);
        setOrdenManual(false);
        setLimiteEvaluacion(A_EVALUAR);
        setCortado(null);
        try {
            const res = await productsAPI.winningSearch({ nicho: texto, limite: 30 });
            const encontrados = res.data.data.products || [];
            setCandidatos(encontrados);

            if (encontrados.length === 0) {
                notify.info(t('winning_products.nothing_found'));
            }
        } catch (error) {
            notify.error(mensajeDeError(error, t('winning_products.search_failed')));
        } finally {
            setBuscando(false);
        }
    };

    // Evaluación a fondo, de uno en uno. El proveedor admite una consulta por
    // segundo y cada producto cuesta además una llamada a la IA: lanzarlas en
    // paralelo era justo lo que dejaba la página vacía.
    useEffect(() => {
        const pendientes = candidatos
            .slice(0, limiteEvaluacion)
            .filter((c) => !c.ganador?.evaluado);
        if (pendientes.length === 0) {
            setPorEvaluar(0);
            return;
        }

        let vigente = true;
        setPorEvaluar(pendientes.length);

        (async () => {
            for (const c of pendientes) {
                if (!vigente) return;

                try {
                    const res = await productsAPI.winningEvaluate({
                        source: c.source,
                        externalId: c.externalId,
                        ...(c.warehouse ? { warehouse: c.warehouse } : {}),
                        lang: i18n.language
                    });

                    if (!vigente) return;

                    const ganador = res.data.data.ganador;
                    setCandidatos((actual) =>
                        actual.map((p) => (p.externalId === c.externalId ? { ...p, ganador } : p))
                    );

                    // Si la cuota del dia se acabo, los que faltan van a fallar
                    // igual: se para aqui y se dice por que, en lugar de gastar
                    // una llamada por producto para llegar a la misma pared.
                    if (ganador?.juicio?.agotado) {
                        setCortado(ganador.juicio.motivo || 'GEMINI_ERROR');
                        setPorEvaluar(0);
                        return;
                    }
                } catch {
                    // Un producto que no se pueda evaluar se queda con su nota
                    // preliminar y se marca como evaluado para no reintentarlo
                    // en bucle.
                    if (!vigente) return;
                    setCandidatos((actual) =>
                        actual.map((p) =>
                            p.externalId === c.externalId
                                ? { ...p, ganador: { ...p.ganador, evaluado: true, fallo: true } }
                                : p
                        )
                    );
                }

                if (vigente) setPorEvaluar((n) => Math.max(0, n - 1));
            }
        })();

        return () => {
            vigente = false;
        };
    }, [candidatos.length, limiteEvaluacion, i18n.language]);

    const filas = useMemo(() => {
        const lista = candidatos.map((c) => {
            const g = c.ganador || {};
            const calc = g.calculables || {};
            const precio = calc.precioObjetivo;
            const costo = calc.costoTotal ?? c.price;

            return {
                producto: c,
                nota: g.nota?.nota ?? 0,
                desglose: g.nota,
                juicio: g.juicio,
                evaluado: Boolean(g.evaluado),
                incompleta: Boolean(g.evaluado) && g.nota?.completa === false,
                motivoSinJuicio: g.juicio && g.juicio.source !== 'gemini' ? g.juicio.motivo || 'GEMINI_ERROR' : null,
                costo,
                envio: g.envio?.costo ?? null,
                precio,
                beneficio: precio !== null && precio !== undefined ? Math.round((precio - costo) * 100) / 100 : null,
                vendedores: calc.vendedores ?? null,
                dias: calc.dias ?? null,
                envioConocido: calc.costoEnvioConocido === true
            };
        });

        const filtradas = lista.filter((f) => f.nota >= notaMinima);

        // Congelado mientras llegan notas, salvo que hayas pedido tú un orden.
        if (porEvaluar > 0 && !ordenManual) return filtradas;

        return [...filtradas].sort((a, b) => {
            const x = a[orden.campo] ?? -1;
            const y = b[orden.campo] ?? -1;
            return orden.desc ? y - x : x - y;
        });
    }, [candidatos, orden, notaMinima, porEvaluar, ordenManual]);

    // El motivo comun a TODOS los evaluados, si lo hay.
    //
    // Solo se afirma cuando la tanda ha terminado. Antes se calculaba en cuanto
    // caia el primero y la pantalla decia «ninguno pudo evaluarse, y todos por
    // lo mismo» con nueve productos todavia sin intentar: una conclusion sacada
    // de una sola muestra.
    const fallaTodo = useMemo(() => {
        if (porEvaluar > 0) return null;
        const evaluadas = filas.filter((f) => f.evaluado);
        if (evaluadas.length === 0) return null;
        const motivos = new Set(evaluadas.map((f) => f.motivoSinJuicio));
        return motivos.size === 1 && [...motivos][0] ? [...motivos][0] : null;
    }, [filas, porEvaluar]);

    // Lo que se le enseña arriba: el corte manda, porque explica ademas por que
    // hay productos sin evaluar.
    const avisoGeneral = cortado || fallaTodo;

    // El filtro por nota solo se pinta cuando puede descartar algo. Con cinco
    // filas, que caben enteras en la pantalla, es un control que no hace nada:
    // ya las estas viendo todas. Se queda visible si tu lo has usado, para no
    // dejar un filtro puesto sin manera de quitarlo.
    const puedeFiltrar = candidatos.length >= A_EVALUAR || notaMinima > 0;

    // Quedan candidatos por evaluar mas alla de la tanda hecha.
    const porVer = candidatos.length - limiteEvaluacion;
    const puedeEvaluarMas = porEvaluar === 0 && !cortado && porVer > 0;

    const ordenarPor = (campo) => {
        setOrdenManual(true);
        setOrden((o) => (o.campo === campo ? { campo, desc: !o.desc } : { campo, desc: true }));
    };

    const importar = async (producto) => {
        if (stores.length === 0) {
            notify.error(t('product_search.connect_store_first'));
            return;
        }

        setImportando((s) => new Set([...s, producto.externalId]));
        try {
            await productsAPI.import({ productData: producto, storeId: stores[0].id });
            notify.success(t('product_search.import_success'));
        } catch (error) {
            notify.error(mensajeDeError(error, t('product_search.import_failed')));
        } finally {
            setImportando((s) => {
                const n = new Set(s);
                n.delete(producto.externalId);
                return n;
            });
        }
    };

    return (
        <div className="space-y-6">
            <div>
                <h1 className="page-title flex items-center gap-2">
                    <HiStar className="w-8 h-8 text-amber-500" />
                    {t('winning_products.title')}
                </h1>
                <p className="text-slate-500 dark:text-slate-400 mt-1">{t('winning_products.subtitle')}</p>
            </div>

            {/* Elegir nicho y buscar */}
            <div className="card space-y-4">
                <div className="flex flex-col lg:flex-row gap-3">
                    <div className="flex-1 relative">
                        <HiSearch className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <input
                            type="text"
                            value={nicho}
                            onChange={(e) => setNicho(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && buscar()}
                            placeholder={t('winning_products.niche_placeholder')}
                            className="input pl-12"
                        />
                    </div>
                    <button type="button" onClick={() => buscar()} disabled={buscando} className="btn-primary">
                        <HiSearch className="w-5 h-5 mr-2" />
                        {buscando ? t('winning_products.searching') : t('winning_products.search')}
                    </button>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-slate-500">{t('winning_products.suggested_niches')}</span>
                    {NICHOS.map((n) => (
                        <button
                            key={n}
                            type="button"
                            onClick={() => {
                                setNicho(t(`winning_products.niche_${n.replace(' ', '_')}`));
                                buscar(t(`winning_products.niche_${n.replace(' ', '_')}`));
                            }}
                            className="px-3 py-1 rounded-lg text-sm bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700"
                        >
                            {t(`winning_products.niche_${n.replace(' ', '_')}`)}
                        </button>
                    ))}
                </div>

                <p className="text-xs text-slate-500">{t('winning_products.criteria_note')}</p>
            </div>

            {/* Si todos los evaluados fallan por el mismo motivo, el problema
                no son los productos. Decirlo una vez arriba ahorra abrir fila
                por fila para descubrir lo mismo treinta veces. */}
            {avisoGeneral && (
                <div className="card !border-l-4 !border-l-amber-500">
                    <p className="text-sm text-amber-800 dark:text-amber-200">
                        <strong>
                            {t(cortado ? 'winning_products.stopped_title' : 'winning_products.all_failed_title')}
                        </strong>{' '}
                        {t(`winning_products.motivo_${avisoGeneral}`, t('winning_products.motivo_GEMINI_ERROR'))}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                        {t(cortado ? 'winning_products.stopped_hint' : 'winning_products.all_failed_hint')}
                    </p>
                </div>
            )}

            {porEvaluar > 0 && (
                <p className="text-sm text-slate-500 dark:text-slate-400" aria-live="polite">
                    {t('winning_products.evaluating', { n: porEvaluar })}
                </p>
            )}

            {candidatos.length > 0 && (
                <>
                    {puedeFiltrar ? (
                        <div className="card flex flex-wrap items-center gap-3">
                            <label htmlFor="notaMin" className="text-sm text-slate-500">
                                {t('winning_products.min_score')}
                            </label>
                            <select
                                id="notaMin"
                                value={notaMinima}
                                onChange={(e) => setNotaMinima(Number(e.target.value))}
                                className="input w-auto"
                            >
                                {[0, 40, 60, 80].map((v) => (
                                    <option key={v} value={v}>{v === 0 ? t('winning_products.all_scores') : `${v}+`}</option>
                                ))}
                            </select>
                            <span className="text-sm text-slate-500">
                                {t('winning_products.showing', { count: filas.length, total: candidatos.length })}
                            </span>
                        </div>
                    ) : (
                        <p className="text-sm text-slate-500 px-1">
                            {t('winning_products.showing', { count: filas.length, total: candidatos.length })}
                        </p>
                    )}

                    <div className="card overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-slate-500 border-b border-slate-200 dark:border-slate-700">
                                    <th className="px-3 py-3 font-medium">{t('winning_products.col_product')}</th>
                                    {COLUMNAS.map((c) => (
                                        <th key={c.clave} className="px-3 py-3 font-medium whitespace-nowrap">
                                            <button
                                                type="button"
                                                onClick={() => ordenarPor(c.clave)}
                                                className="hover:text-slate-900 dark:hover:text-white"
                                            >
                                                {t(`winning_products.col_${c.etiqueta}`)}
                                                {orden.campo === c.clave && (orden.desc ? ' ↓' : ' ↑')}
                                            </button>
                                        </th>
                                    ))}
                                    <th className="px-3 py-3"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filas.map((f) => (
                                    <Fragment key={f.producto.externalId}>
                                        <tr
                                            className={
                                                abierto === f.producto.externalId
                                                    ? 'bg-slate-50 dark:bg-slate-700/40'
                                                    : 'border-b border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/40'
                                            }
                                        >
                                            <td className="px-3 py-3">
                                                <div className="flex items-center gap-3 min-w-[220px]">
                                                    <img
                                                        src={f.producto.mainImage || PRODUCT_IMAGE_FALLBACK}
                                                        alt=""
                                                        className="w-10 h-10 rounded object-cover flex-shrink-0"
                                                    />
                                                    <div className="min-w-0">
                                                        <p className="line-clamp-2 text-slate-900 dark:text-white">
                                                            {f.producto.title}
                                                        </p>
                                                        {f.juicio?.problema && (
                                                            <p className="text-xs text-slate-500 line-clamp-1">
                                                                {f.juicio.problema}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Una nota baja porque el producto es malo y una
                                                nota baja porque no se pudo evaluar la mitad de
                                                los criterios NO son lo mismo, y en rojo se leen
                                                igual. Se dice cual es cual. */}
                                            <td className="px-3 py-3">
                                                <span className={`font-bold ${
                                                    !f.evaluado || f.incompleta
                                                        ? 'text-slate-500 dark:text-slate-400'
                                                        : colorDeNota(f.nota)
                                                }`}>
                                                    {f.nota}
                                                </span>
                                                {!f.evaluado ? (
                                                    <span className="block text-[11px] text-slate-400">
                                                        {t('winning_products.preliminary')}
                                                    </span>
                                                ) : f.incompleta ? (
                                                    <span className="block text-[11px] text-amber-600 dark:text-amber-400">
                                                        {t('winning_products.of_assessable', {
                                                            cobertura: f.desglose.cobertura
                                                        })}
                                                    </span>
                                                ) : null}
                                            </td>

                                            <td className="px-3 py-3 tabular-nums">{dinero(f.costo)}</td>
                                            <td className="px-3 py-3 tabular-nums">
                                                {f.envio !== null ? dinero(f.envio) : <span className="text-slate-400">—</span>}
                                            </td>
                                            <td className="px-3 py-3 tabular-nums">
                                                {f.precio !== null ? dinero(f.precio) : '—'}
                                            </td>
                                            <td className="px-3 py-3 tabular-nums text-green-600 dark:text-green-400">
                                                {f.beneficio !== null ? dinero(f.beneficio) : '—'}
                                            </td>
                                            <td className="px-3 py-3 tabular-nums">
                                                {f.vendedores !== null ? f.vendedores.toLocaleString() : '—'}
                                            </td>
                                            <td className="px-3 py-3 tabular-nums">
                                                {f.dias !== null ? f.dias : '—'}
                                            </td>

                                            <td className="px-3 py-3">
                                                <div className="flex items-center gap-1">
                                                    <button
                                                        type="button"
                                                        onClick={() => setAbierto(abierto === f.producto.externalId ? null : f.producto.externalId)}
                                                        className="icon-view"
                                                        title={t('winning_products.why')}
                                                    >
                                                        <HiChevronDown className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setFicha(f.producto)}
                                                        className="icon-view"
                                                        title={t('imported_products.view')}
                                                    >
                                                        <HiSearch className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => importar(f.producto)}
                                                        disabled={importando.has(f.producto.externalId)}
                                                        className="icon-publish"
                                                        title={t('product_search.import')}
                                                    >
                                                        <HiShoppingCart className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>

                                        {/* De qué está hecha la nota */}
                                        {abierto === f.producto.externalId && f.desglose && (
                                            <tr className="bg-slate-50 dark:bg-slate-700/40 border-b border-slate-200 dark:border-slate-700">
                                                <td colSpan={COLUMNAS.length + 2} className="px-3 py-4">
                                                    <div className="space-y-2 max-w-3xl">
                                                        {f.desglose.partes.map((p) => (
                                                            <div key={p.criterio} className="flex items-baseline gap-3 text-sm">
                                                                <span className="w-40 flex-shrink-0 text-slate-500">
                                                                    {t(`winning_products.criterio_${p.criterio}`)}
                                                                </span>
                                                                <span className={`w-24 flex-shrink-0 font-medium ${
                                                                    p.valor === null ? 'text-slate-400' : p.valor >= 0.5 ? 'text-green-600 dark:text-green-400' : 'text-red-500'
                                                                }`}>
                                                                    {p.valor === null
                                                                        ? t('winning_products.not_measured')
                                                                        : `${Math.round(p.valor * p.peso)} / ${p.peso}`}
                                                                </span>
                                                                <span className="text-slate-600 dark:text-slate-300">{p.detalle || ''}</span>
                                                            </div>
                                                        ))}

                                                        {f.desglose.limitadaPor?.length > 0 && (
                                                            <p className="text-sm text-amber-700 dark:text-amber-300 pt-2">
                                                                {t('winning_products.capped_by', {
                                                                    criterios: f.desglose.limitadaPor
                                                                        .map((c) => t(`winning_products.criterio_${c}`))
                                                                        .join(', ')
                                                                })}
                                                            </p>
                                                        )}

                                                        {f.desglose.completa === false && (
                                                            <p className="text-xs text-slate-500">
                                                                {t('winning_products.coverage', { n: f.desglose.cobertura })}
                                                                {f.motivoSinJuicio && (
                                                                    <> {t(`winning_products.motivo_${f.motivoSinJuicio}`, t('winning_products.motivo_GEMINI_ERROR'))}</>
                                                                )}
                                                            </p>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Al final de la lista, que es donde acabas de leer y donde
                        se busca un «cargar mas». Antes vivia en la barra de
                        filtros de arriba: una accion metida entre controles de
                        filtrado, lejos de la mirada de quien termina la tabla. */}
                    {puedeEvaluarMas && (
                        <div className="flex justify-center">
                            <button
                                type="button"
                                onClick={() => setLimiteEvaluacion((l) => l + A_EVALUAR)}
                                className="btn-secondary"
                            >
                                {t('winning_products.evaluate_more', { n: Math.min(A_EVALUAR, porVer) })}
                            </button>
                        </div>
                    )}
                </>
            )}

            {/* Estado inicial: se dice qué hay que hacer, no «vuelve más tarde» */}
            {candidatos.length === 0 && !buscando && (
                <div className="card text-center py-16">
                    <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
                        <HiStar className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
                        {t('winning_products.empty_title')}
                    </h3>
                    <p className="text-slate-500 max-w-lg mx-auto">{t('winning_products.empty_desc')}</p>
                </div>
            )}

            {ficha && (
                <ProductDetailsModal
                    vista={desdeCandidato(ficha)}
                    comision={comisionDe(stores[0]?.platform, stores[0]?.settings)}
                    importing={importando.has(ficha.externalId)}
                    onImport={importar}
                    onClose={() => setFicha(null)}
                />
            )}
        </div>
    );
}
